import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Upload } from "lucide-react";

const COUNTRIES = [
  "United States", "Canada", "United Kingdom", "Australia", "Germany", "France",
  "India", "Japan", "China", "Brazil", "Mexico", "Spain", "Italy", "Netherlands",
  "Sweden", "United Arab Emirates", "Singapore", "Other",
];

const initial = {
  first_name: "", last_name: "", email: "", phone: "",
  company_name: "", company_website: "",
  country: "", state: "", city: "",
  address1: "", address2: "", zip_code: "",
  tax_id: "",
};

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function RegistrationForm() {
  const [form, setForm] = useState(initial);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (file) {
        payload.document_name = file.name;
        payload.document_base64 = await fileToBase64(file);
      }
      await api.post("/customers/register", payload);
      setSuccess(true);
      toast.success("Application submitted for review");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to submit";
      toast.error(typeof msg === "string" ? msg : "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "#f6f6f7" }}>
        <div className="b2b-card max-w-md w-full p-8 text-center" data-testid="registration-success">
          <div
            className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4"
            style={{ background: "#cdf5e1", color: "#005c46" }}
          >
            <CheckCircle2 size={28} />
          </div>
          <h2 className="text-xl font-semibold mb-2" style={{ color: "var(--brand-navy)" }}>
            Application received
          </h2>
          <p className="text-sm" style={{ color: "var(--brand-muted)" }}>
            Thanks for applying to become a wholesale partner. Our team will review your application and email you
            at <strong>{form.email}</strong> once a decision is made.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-10 px-4" style={{ background: "#f6f6f7" }} data-testid="registration-form-page">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <div
            className="inline-flex items-center justify-center w-12 h-12 rounded-lg font-bold mb-3"
            style={{ background: "var(--brand-navy)", color: "var(--brand-yellow)" }}
          >
            V
          </div>
          <h1 className="text-3xl font-semibold" style={{ color: "var(--brand-navy)" }}>
            Wholesale Application
          </h1>
          <p className="mt-2 text-sm" style={{ color: "var(--brand-muted)" }}>
            Apply to purchase at wholesale pricing. All fields are reviewed by our team.
          </p>
        </div>

        <form onSubmit={submit} className="b2b-card p-6 space-y-6" data-testid="registration-form">
          <section>
            <h3 className="text-base font-semibold mb-4" style={{ color: "var(--brand-navy)" }}>
              Personal Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">First name *</label>
                <input required className="form-input" value={form.first_name} onChange={set("first_name")} data-testid="reg-first-name" />
              </div>
              <div>
                <label className="form-label">Last name *</label>
                <input required className="form-input" value={form.last_name} onChange={set("last_name")} data-testid="reg-last-name" />
              </div>
              <div>
                <label className="form-label">Email *</label>
                <input required type="email" className="form-input" value={form.email} onChange={set("email")} data-testid="reg-email" />
              </div>
              <div>
                <label className="form-label">Phone number *</label>
                <input required className="form-input" value={form.phone} onChange={set("phone")} data-testid="reg-phone" />
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-base font-semibold mb-4" style={{ color: "var(--brand-navy)" }}>
              Company Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">Company name *</label>
                <input required className="form-input" value={form.company_name} onChange={set("company_name")} data-testid="reg-company-name" />
              </div>
              <div>
                <label className="form-label">Company website</label>
                <input className="form-input" placeholder="https://" value={form.company_website} onChange={set("company_website")} data-testid="reg-company-website" />
              </div>
              <div>
                <label className="form-label">Tax Identification Number</label>
                <input className="form-input" value={form.tax_id} onChange={set("tax_id")} data-testid="reg-tax-id" />
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-base font-semibold mb-4" style={{ color: "var(--brand-navy)" }}>
              Address
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">Country *</label>
                <select required className="form-select" value={form.country} onChange={set("country")} data-testid="reg-country">
                  <option value="">Select country</option>
                  {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">State / Province *</label>
                <input required className="form-input" value={form.state} onChange={set("state")} data-testid="reg-state" />
              </div>
              <div>
                <label className="form-label">City *</label>
                <input required className="form-input" value={form.city} onChange={set("city")} data-testid="reg-city" />
              </div>
              <div>
                <label className="form-label">Zip code *</label>
                <input required className="form-input" value={form.zip_code} onChange={set("zip_code")} data-testid="reg-zip" />
              </div>
              <div className="md:col-span-2">
                <label className="form-label">Address line 1 *</label>
                <input required className="form-input" value={form.address1} onChange={set("address1")} data-testid="reg-address1" />
              </div>
              <div className="md:col-span-2">
                <label className="form-label">Address line 2</label>
                <input className="form-input" value={form.address2} onChange={set("address2")} data-testid="reg-address2" />
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-base font-semibold mb-4" style={{ color: "var(--brand-navy)" }}>
              Business Documents
            </h3>
            <label
              className="flex items-center gap-3 border-2 border-dashed rounded-lg px-4 py-6 cursor-pointer hover:bg-gray-50"
              style={{ borderColor: "var(--brand-border)" }}
              data-testid="reg-file-upload-label"
            >
              <Upload size={18} style={{ color: "var(--brand-muted)" }} />
              <span className="text-sm" style={{ color: "var(--brand-muted)" }}>
                {file ? file.name : "Upload business license, resale certificate, etc. (PDF/Image, up to 10 MB)"}
              </span>
              <input
                type="file"
                className="hidden"
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="reg-file-upload"
              />
            </label>
          </section>

          <button type="submit" disabled={submitting} className="btn-primary w-full" data-testid="reg-submit">
            {submitting ? "Submitting…" : "Submit Application"}
          </button>
        </form>
      </div>
    </div>
  );
}
